import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import Logo from '../../assets/Logo_no_bg.png';

const Navigation: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isDocsOpen, setIsDocsOpen] = useState(false);
  const [isMobileDocsOpen, setIsMobileDocsOpen] = useState(false);
  const loginDropdownRef = useRef<HTMLDivElement>(null);
  const docsDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileDocsOpen(false);
    setIsDocsOpen(false);
    setIsLoginOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (loginDropdownRef.current && !loginDropdownRef.current.contains(event.target as Node)) {
        setIsLoginOpen(false);
      }
      if (docsDropdownRef.current && !docsDropdownRef.current.contains(event.target as Node)) {
        setIsDocsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'Services', path: '/services' },
    { name: 'Pricing', path: '/pricing' },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'glass py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
            aria-label="Cimmple Home"
          >
            <img 
              src={Logo} 
              alt="Cimmple Logo" 
              className="h-10 w-auto transform group-hover:scale-105 transition-transform duration-300 dark:brightness-0 dark:invert"
            />
            <span className="text-2xl font-display font-bold text-slate-900 dark:text-white">
              Cimmple
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                  isActive(link.path)
                    ? 'text-primary-700 dark:text-white bg-primary-50 dark:bg-dark-800/50'
                    : 'text-slate-700 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-dark-800/30'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="relative" ref={docsDropdownRef}>
              <button
                onClick={() => setIsDocsOpen(!isDocsOpen)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center gap-1 ${
                  location.pathname.startsWith('/docs') || location.pathname.startsWith('/help')
                    ? 'text-primary-700 dark:text-white bg-primary-50 dark:bg-dark-800/50'
                    : 'text-slate-700 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-dark-800/30'
                }`}
              >
                Docs
                <svg className={`w-4 h-4 transition-transform ${isDocsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isDocsOpen && (
                <div className="absolute left-0 mt-2 w-[28rem] p-3 bg-white dark:bg-dark-900 rounded-xl shadow-lg border border-slate-200 dark:border-dark-800">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg p-3 border border-slate-200 dark:border-dark-800">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-dark-400 mb-2">CimmpleFlow</p>
                      <Link
                        to="/docs/cimmpleflow"
                        onClick={() => setIsDocsOpen(false)}
                        className="block px-2 py-2 rounded-md text-slate-700 dark:text-dark-300 hover:bg-slate-100 dark:hover:bg-dark-800 hover:text-slate-900 dark:hover:text-white transition-colors"
                      >
                        Help Center
                      </Link>
                    </div>
                    <div className="rounded-lg p-3 border border-slate-200 dark:border-dark-800">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-dark-400 mb-2">CimmplePay</p>
                      <Link
                        to="/docs/cimmplepay"
                        onClick={() => setIsDocsOpen(false)}
                        className="block px-2 py-2 rounded-md text-slate-700 dark:text-dark-300 hover:bg-slate-100 dark:hover:bg-dark-800 hover:text-slate-900 dark:hover:text-white transition-colors"
                      >
                        API Docs
                      </Link>
                      <Link
                        to="/help/cimmplepay"
                        onClick={() => setIsDocsOpen(false)}
                        className="block px-2 py-2 rounded-md text-slate-700 dark:text-dark-300 hover:bg-slate-100 dark:hover:bg-dark-800 hover:text-slate-900 dark:hover:text-white transition-colors"
                      >
                        Help Center
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Link
              to="/contact"
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                isActive('/contact')
                  ? 'text-primary-700 dark:text-white bg-primary-50 dark:bg-dark-800/50'
                  : 'text-slate-700 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-dark-800/30'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* CTA & Theme Toggle */}
          <div className="hidden md:flex items-center gap-3">
            {/* Login Dropdown */}
            <div className="relative" ref={loginDropdownRef}>
              <button
                onClick={() => setIsLoginOpen(!isLoginOpen)}
                className="px-4 py-2 rounded-lg font-medium text-slate-700 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-dark-800/30 transition-all duration-300 flex items-center gap-1"
              >
                Login
                <svg className={`w-4 h-4 transition-transform ${isLoginOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isLoginOpen && (
                <div className="absolute right-0 mt-2 w-56 py-2 bg-white dark:bg-dark-900 rounded-xl shadow-lg border border-slate-200 dark:border-dark-800">
                  <a
                    href="https://cimmple.net"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsLoginOpen(false)}
                    className="block px-4 py-2 text-slate-700 dark:text-dark-300 hover:bg-slate-100 dark:hover:bg-dark-800 hover:text-slate-900 dark:hover:text-white transition-colors"
                  >
                    CimmpleFlow
                  </a>
                  <a
                    href="https://payroll.cimmple.net"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsLoginOpen(false)}
                    className="block px-4 py-2 text-slate-700 dark:text-dark-300 hover:bg-slate-100 dark:hover:bg-dark-800 hover:text-slate-900 dark:hover:text-white transition-colors"
                  >
                    CimmplePay
                  </a>
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-lg bg-slate-100 dark:bg-dark-800/50 hover:bg-slate-200 dark:hover:bg-dark-700/50 transition-all duration-300 border border-slate-200 dark:border-transparent"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? (
                <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-slate-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>
            
            <Link to="/contact" className="btn-primary">
              Get Started
            </Link>
          </div>

          {/* Mobile: Theme Toggle & Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-slate-100 dark:bg-dark-800/50 hover:bg-slate-200 dark:hover:bg-dark-700/50 transition-colors border border-slate-200 dark:border-transparent"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? (
                <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-slate-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>
            
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-dark-800/50 transition-colors"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <svg
                className="w-6 h-6 text-slate-900 dark:text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-out ${
            isMobileMenuOpen ? 'max-h-[700px] opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="bg-white dark:bg-dark-900 rounded-2xl p-4 space-y-2 border border-slate-200 dark:border-dark-800 shadow-lg dark:shadow-none">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                  isActive(link.path)
                    ? 'text-primary-700 dark:text-white bg-primary-50 dark:bg-dark-800/50'
                    : 'text-slate-700 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-dark-800/30'
                }`}
              >
                {link.name}
              </Link>
            ))}

            <div className="rounded-xl border border-slate-200 dark:border-dark-800">
              <button
                onClick={() => setIsMobileDocsOpen(!isMobileDocsOpen)}
                className={`w-full px-4 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-between ${
                  location.pathname.startsWith('/docs') || location.pathname.startsWith('/help')
                    ? 'text-primary-700 dark:text-white bg-primary-50 dark:bg-dark-800/50'
                    : 'text-slate-700 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-dark-800/30'
                }`}
                aria-expanded={isMobileDocsOpen}
              >
                Docs
                <svg className={`w-4 h-4 transition-transform ${isMobileDocsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isMobileDocsOpen && (
                <div className="px-2 pb-2">
                  <p className="px-2 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-dark-400">CimmpleFlow</p>
                  <Link to="/docs/cimmpleflow" className="block px-3 py-2 rounded-lg text-slate-700 dark:text-dark-300 hover:bg-slate-100 dark:hover:bg-dark-800/30">Help Center</Link>
                  <p className="px-2 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-dark-400">CimmplePay</p>
                  <Link to="/docs/cimmplepay" className="block px-3 py-2 rounded-lg text-slate-700 dark:text-dark-300 hover:bg-slate-100 dark:hover:bg-dark-800/30">API Docs</Link>
                  <Link to="/help/cimmplepay" className="block px-3 py-2 rounded-lg text-slate-700 dark:text-dark-300 hover:bg-slate-100 dark:hover:bg-dark-800/30">Help Center</Link>
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className={`block px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                isActive('/contact')
                  ? 'text-primary-700 dark:text-white bg-primary-50 dark:bg-dark-800/50'
                  : 'text-slate-700 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-dark-800/30'
              }`}
            >
              Contact
            </Link>
            
            {/* Login Links */}
            <div className="pt-2 mt-2 border-t border-slate-200 dark:border-dark-800">
              <p className="px-4 py-2 text-sm text-slate-500 dark:text-dark-400 font-medium">Login to:</p>
              <a
                href="https://cimmple.net"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-3 rounded-xl font-medium text-slate-700 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-dark-800/30 transition-all duration-300"
              >
                CimmpleFlow
              </a>
              <a
                href="https://payroll.cimmple.net"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-3 rounded-xl font-medium text-slate-700 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-dark-800/30 transition-all duration-300"
              >
                CimmplePay
              </a>
            </div>
            
            <Link to="/contact" className="btn-primary block text-center mt-4">
              Get Started
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navigation;
